/**
 * Stage geometry of a HoloToken, in fractions of the token box.
 *
 * These are the same numbers HoloToken.css lays out from — they live here so
 * anything that has to place a token on a map derives the anchor arithmetically
 * instead of eyeballing a percentage that then drifts when the art changes.
 * Keep the two in step: every value below has a `--ht-*` twin in the stylesheet.
 */
export const HOLO_GEOMETRY = {
  /** pedestal artwork aspect ratio, 512 / 356 */
  baseAr: 1.4382,
  /** token height / token width */
  tokenAr: 1.55,
  /** pedestal width, fraction of token width */
  baseW: 0.84,
  /** pedestal bottom, fraction of token height */
  baseB: 0.055,
} as const;

/** Pedestal height as a fraction of token height. */
export const HOLO_BASE_H =
  HOLO_GEOMETRY.baseW / HOLO_GEOMETRY.baseAr / HOLO_GEOMETRY.tokenAr;

/**
 * Where the pedestal plate's centre sits, measured down from the top of the
 * token box. Anchoring this point on a grid cell puts the plate in the cell and
 * lets the projection rise out of it.
 */
export const HOLO_BASE_CENTER_Y = 1 - (HOLO_GEOMETRY.baseB + HOLO_BASE_H / 2);

/**
 * The plate's contact ellipse, in fractions of the pedestal image. Measured off the
 * artwork's alpha: widest scanline at y=227/356 spanning x=5..508 of 512, and the
 * foreshortening the artwork was rendered at, cos(68deg).
 *
 * Anything drawn on the floor under a token must use THESE numbers and the same
 * ground rotation, not the host cell's own ellipse — the pedestal is rendered from a
 * lower camera than the map, so a shadow shaped to the cell would never sit on it.
 * Mirrors the --ht-ground-* block in HoloToken.css.
 */
export const HOLO_GROUND = {
  cx: 0.501,
  cy: 0.6376,
  rx: 0.4912,
  squash: 0.3746,
} as const;

/**
 * Where the plate actually touches the floor, measured down from the top of the token
 * box. This sits below the plate's box centre, because the contact ellipse is low in
 * the artwork.
 *
 * Anchor a token here rather than on HOLO_BASE_CENTER_Y and the point the plate stands
 * on lands on the cell, instead of the middle of the sprite landing on it. That matters
 * as soon as the plate is transformed to lie in the floor: the transform pivots about
 * the contact point, so anchoring anywhere else slides the plate out from under its own
 * projection.
 */
export const HOLO_CONTACT_CENTER_Y =
  HOLO_BASE_CENTER_Y + (HOLO_GROUND.cy - 0.5) * HOLO_BASE_H;

/**
 * How far the plate's contact point sits below the centre of the pedestal image,
 * as a fraction of the pedestal's rendered WIDTH. The token is anchored by its box
 * centre, so grounding needs this to find where it actually meets the floor.
 */
export const HOLO_CONTACT_DROP =
  (HOLO_GROUND.cy - 0.5) / HOLO_GEOMETRY.baseAr;

/** Token width that makes the pedestal exactly `cellWidth` wide. */
export function holoWidthForBase(baseWidthPx: number): number {
  return baseWidthPx / HOLO_GEOMETRY.baseW;
}
